// Sessions — the logins you can revoke.
//
// Was a collapsed accordion whose every session printed three labelled date lines,
// which made four sessions a 12-line block. One row each now: the id, then started /
// last access / expires on one line of secondary text, with revoke on the trailing
// edge. The revoke confirm is unchanged — it is a write, and it keeps its dialog.
import { useCallback, useState } from 'react';
import {
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '../../../ui/core';
import { scrollY } from '../../../ui/styles';
import { LogoutIcon, InfoIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters, useToast } from '../../../hooks';
import { getSessions, revokeSession } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  TrailingMeta,
  Meta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

const SessionRow = ({ session, waiting, onRevoke, first }) => {
  const f = useFormatters();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const at = (v) => f.date.toLocaleString(v, f.date.DateFormats.DATETIME_MED);

  return (
    <>
      <Row
        first={first}
        label={session.id}
        meta={`Started ${at(session.startedAt)} · last access ${at(session.lastAccessedAt)} · expires ${at(
          session.expiresAt,
        )}`}
        right={
          <IconButton
            size="small"
            disabled={waiting}
            aria-label={`Revoke session ${session.id}`}
            title="Revoke session"
            onClick={() => setShowConfirmDialog(true)}
            sx={{ width: 26, height: 26, color: 'text.secondary' }}
          >
            {waiting ? <CircularProgress size={15} color="inherit" /> : <LogoutIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        }
      />
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogContent sx={{ maxWidth: '400px' }}>
          <Typography>Are you sure you want to logout</Typography>
          <Typography sx={{ fontWeight: 700 }} component="span">
            {session.id}
          </Typography>
          ?
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
              onRevoke(session);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SessionsCard = ({ user }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const [waitingFor, setWaitingFor] = useState(null);
  const data = useSelector((state) => state.users.selection.sessions);

  // Was `useEffect(…, [])`, so opening a second user from search kept the first one's
  // sessions on screen.
  const load = useCallback(() => getSessions(user.id)(dispatch), [user.id, dispatch]);
  const [status, retry] = useSectionFetch(load);

  const doRevokeSession = async (session) => {
    setWaitingFor(session.id);
    const { success } = await revokeSession(user.id, session.id)(dispatch);
    success ? t.success(`Session has been revoked`) : t.error(`Something went wrong`);
    setWaitingFor(null);
  };

  // Copied before sorting: `useSelector` hands back the array in the store, and
  // `Array.prototype.sort` mutates in place — sorting it here rewrote reducer state
  // from a render.
  const sessions = data ? [...data].sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1)) : null;

  return (
    <SectionCard
      title="Sessions"
      icon={<InfoIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        sessions ? (
          <TrailingMeta>
            <Meta>{sessions.length} active</Meta>
          </TrailingMeta>
        ) : null
      }
    >
      {status === 'loading' && <SectionSkeleton />}
      {status === 'failed' && <SectionError onRetry={retry}>Couldn&rsquo;t load sessions.</SectionError>}
      {status === 'done' && !sessions?.length && <EmptyRows>No active sessions.</EmptyRows>}
      {status === 'done' && sessions?.length > 0 && (
        <Box sx={{ maxHeight: 300, ...scrollY }}>
          {sessions.map((s, i) => (
            <SessionRow
              key={s.id}
              first={i === 0}
              session={s}
              waiting={waitingFor === s.id}
              onRevoke={doRevokeSession}
            />
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default SessionsCard;
